/**
 * The Activity log, said the way a person would say it.
 *
 * Each entry carries a little record of what it touched — how many candidates,
 * which class, whether the papers were shuffled. It was printed as the data
 * holds it: "shuffled: false · papersSet: 1 · groupName: null". Accurate, and
 * written for whoever built the platform rather than whoever runs a centre.
 */

/** What a role is called out loud. */
export const ROLE_NAME: Record<string, string> = {
  SUPER_ADMIN: 'Platform admin',
  INSTRUCTOR: 'Teacher',
  STUDENT: 'Candidate',
};

export const roleName = (role: string): string => ROLE_NAME[role] ?? sentence(role);

/** The names the log's details go by, where the plain words would mislead. */
const LABEL: Record<string, string> = {
  phone: 'Sign-in number',
  groupName: 'Class',
  enrolledTo: 'Enrolled to',
  papersSet: 'Papers set',
  candidates: 'Candidates',
  shuffled: 'Papers dealt around the room',
  browserExamKeys: 'Browser exam keys',
  configKeys: 'Config keys',
  forSignInSlip: 'For a sign-in slip',
  testId: 'Paper',
  studentId: 'Candidate',
};

/** "papersSet" → "Papers set"; "WRITING" → "Writing". */
function sentence(value: string): string {
  const spaced = value
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** An id is not something a person reads; the entry's own subject names the thing. */
const looksLikeId = (value: unknown): boolean =>
  typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(value);

function say(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (Array.isArray(value)) {
    const items = value.map(say).filter((v): v is string => Boolean(v));
    return items.length ? items.join(', ') : null;
  }
  if (typeof value === 'string') {
    // Codes held in capitals — WRITING, PUBLISHED — read as words.
    return /^[A-Z][A-Z_]+$/.test(value) ? sentence(value) : value;
  }
  if (typeof value === 'number') return String(value);
  return null;
}

/**
 * One entry's details as short phrases, nothing empty and nothing that is only
 * an id: "Papers set 1 · Candidates 1 · Papers dealt around the room: no".
 */
export function describeDetail(detail: Record<string, unknown> | null | undefined): string[] {
  if (!detail) return [];
  const out: string[] = [];
  for (const [key, raw] of Object.entries(detail)) {
    if (looksLikeId(raw)) continue;
    const value = say(raw);
    if (value === null) continue;
    const label = LABEL[key] ?? sentence(key);
    // A yes or no reads better after a colon; a count or a name after a space.
    out.push(typeof raw === 'boolean' ? `${label}: ${value}` : `${label} ${value}`);
  }
  return out;
}
